/**
 * Per-domain web client for the People Directory & Profiles vertical.
 * Thin typed wrappers over the shared {@link apiFetch} helper.
 */
import type {
  ContactInput,
  ContactUpdateInput,
  CustomField,
  CustomFieldInput,
  CustomFieldUpdateInput,
  CustomValueInput,
  DirectoryEntry,
  Paginated,
  PersonContact,
  PersonCustomValue,
  PersonProfile,
  PersonRelationship,
  RelationshipInput,
  RelationshipUpdateInput,
} from '@fc/core'
import { apiFetch } from './api'

export type {
  ContactInput,
  ContactKind,
  ContactUpdateInput,
  CustomField,
  CustomFieldInput,
  CustomFieldType,
  CustomFieldUpdateInput,
  CustomValueInput,
  DirectoryEntry,
  PersonContact,
  PersonCustomValue,
  PersonProfile,
  PersonRelationship,
  RelationshipInput,
  RelationshipType,
  RelationshipUpdateInput,
} from '@fc/core'

const qs = (params: object): string => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ---- Directory ----
export type DirectoryQuery = {
  q?: string
  status?: string
  page?: number
  pageSize?: number
}

export const listDirectory = (params: DirectoryQuery = {}) =>
  apiFetch<Paginated<DirectoryEntry>>(`/directory${qs(params)}`)

// ---- Profile ----
export const getPersonProfile = (id: string) => apiFetch<PersonProfile>(`/people/${id}`)

// ---- Contacts ----
export const addContact = (personId: string, input: ContactInput) =>
  apiFetch<PersonContact>(`/people/${personId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateContact = (personId: string, contactId: string, input: ContactUpdateInput) =>
  apiFetch<PersonContact>(`/people/${personId}/contacts/${contactId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteContact = (personId: string, contactId: string) =>
  apiFetch<void>(`/people/${personId}/contacts/${contactId}`, { method: 'DELETE' })

// ---- Relationships ----
export const addRelationship = (personId: string, input: RelationshipInput) =>
  apiFetch<PersonRelationship>(`/people/${personId}/relationships`, {
    method: 'POST',
    body: JSON.stringify(input),
  })

export const updateRelationship = (
  personId: string,
  relId: string,
  input: RelationshipUpdateInput,
) =>
  apiFetch<PersonRelationship>(`/people/${personId}/relationships/${relId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteRelationship = (personId: string, relId: string) =>
  apiFetch<void>(`/people/${personId}/relationships/${relId}`, { method: 'DELETE' })

// ---- Custom fields (admin) ----
export const listCustomFields = (entityType = 'person') =>
  apiFetch<CustomField[]>(`/custom-fields${qs({ entityType })}`)

export const createCustomField = (input: CustomFieldInput) =>
  apiFetch<CustomField>('/custom-fields', { method: 'POST', body: JSON.stringify(input) })

export const updateCustomField = (fieldId: string, input: CustomFieldUpdateInput) =>
  apiFetch<CustomField>(`/custom-fields/${fieldId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  })

export const deleteCustomField = (fieldId: string) =>
  apiFetch<void>(`/custom-fields/${fieldId}`, { method: 'DELETE' })

// ---- Custom values ----
export const getCustomValues = (personId: string) =>
  apiFetch<PersonCustomValue[]>(`/people/${personId}/custom-values`)

export const saveCustomValues = (personId: string, values: CustomValueInput[]) =>
  apiFetch<PersonCustomValue[]>(`/people/${personId}/custom-values`, {
    method: 'PUT',
    body: JSON.stringify({ values }),
  })
